<template>
  <div
    class="block-banner border-b text-sm"
    :class="bannerClasses"
    :dir="dir"
  >
    <div class="container mx-auto flex flex-wrap items-center justify-center gap-3 px-4 py-2.5 sm:px-6 lg:px-8">
      <UIcon
        v-if="icon"
        :name="icon"
        class="size-5 shrink-0"
      />
      <p class="flex-1 text-center font-medium md:text-start">
        {{ resolvedTitle }}
      </p>
      <UButton
        v-if="resolvedCtaLabel"
        size="xs"
        variant="solid"
        :color="ctaColor"
        :to="builderPreview ? undefined : ctaTo"
        @click="builderPreview && ctaTo ? handleNavClick(ctaTo) : undefined"
      >
        {{ resolvedCtaLabel }}
      </UButton>
    </div>
  </div>
</template>

<script setup lang="ts">
const dir = useDir()
const pageLocale = useState<string>('page-locale', () => 'fa')

type LocalizedString = string | Record<string, string>
type BannerColor = 'primary' | 'neutral' | 'success' | 'warning' | 'error' | 'info'

const props = withDefaults(
  defineProps<{
    icon?: string
    title?: LocalizedString
    ctaLabel?: LocalizedString
    ctaTo?: string
    color?: BannerColor
    ctaColor?: BannerColor
    builderPreview?: boolean
    onNavigate?: (to: string) => void
  }>(),
  {
    color: 'primary',
    ctaColor: 'neutral',
    builderPreview: false,
  },
)

const ctaColor = computed(() => {
  const allowedColors: BannerColor[] = ['primary', 'neutral', 'success', 'warning', 'error', 'info']
  return allowedColors.includes(props.ctaColor ?? 'neutral') ? props.ctaColor ?? 'neutral' : 'neutral'
})

function resolveVal(value: unknown, loc: string): any {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.map((v) => resolveVal(v, loc))
  if (typeof value === 'object') {
    const rec = value as Record<string, unknown>
    if (rec[loc] !== undefined) return rec[loc]
    return Object.fromEntries(
      Object.entries(rec).map(([k, v]) => [k, resolveVal(v, loc)]),
    )
  }
  return value
}

const resolvedTitle = computed(() => {
  const t = props.title
  if (typeof t === 'string') return t
  return t ? String(resolveVal(t, pageLocale.value)) : ''
})

const resolvedCtaLabel = computed(() => {
  const t = props.ctaLabel
  if (typeof t === 'string') return t
  return t ? String(resolveVal(t, pageLocale.value)) : ''
})

const bannerClasses = computed(() => {
  switch (props.color) {
    case 'neutral':
      return 'bg-gray-900 text-white border-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-200'
    case 'success':
      return 'bg-emerald-50 text-emerald-900 border-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-900/40'
    case 'warning':
      return 'bg-amber-50 text-amber-900 border-amber-100 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-900/40'
    case 'error':
      return 'bg-red-50 text-red-900 border-red-100 dark:bg-red-950/40 dark:text-red-200 dark:border-red-900/40'
    case 'info':
      return 'bg-sky-50 text-sky-900 border-sky-100 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-900/40'
    case 'primary':
    default:
      return 'block-banner--primary border-transparent text-white'
  }
})

function handleNavClick(to: string | undefined) {
  if (!to) return
  if (props.builderPreview && props.onNavigate) props.onNavigate(to)
}
</script>

<style scoped>
.block-banner--primary {
  background-color: var(--ui-primary, #2563eb);
}
</style>
